import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthService } from '../services/authService';
import { getThemeAssets } from '../utils/theme';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Banner data for carousel
  const bannerData = [
    {
      image: '/assets/banners/farmer-01.jpg',
      title: 'Harmony with Nature',
      subtitle: 'Community Empowerment',
      description: 'Explore well-being with ZeroHarm Sciences Pvt Ltd. In harmony with nature, we collaborate with 10,000+ Northeast Indian farmers for premium nutraceutical ingredients. Prioritizing sustainability and community empowerment, we guide farmers in cultivating medicinal plants, ensuring ethical and responsible sourcing.',
      stats: []
    },
    {
      image: '/assets/banners/Institute.01.jpg',
      title: 'Innovation & Research',
      subtitle: 'Clinical Excellence',
      description: 'ZeroHarm Sciences pioneers innovation, collaborating with top institutions for rigorous clinical studies. Our commitment to excellence ensures scientifically validated, transparent, and safe nutraceuticals for evidence-based wellness solutions.',
      stats: []
    },
    {
      image: '/assets/banners/Critical_unit-01.jpg',
      title: 'Supercritical Extraction',
      subtitle: 'Cutting-edge Technology',
      description: 'Enhance your health with ZeroHarm Sciences Pvt Ltd\'s cutting-edge supercritical extraction. Operating at Guwahati Biotech Park, our advanced process ensures pure, potent compounds for unparalleled nutraceutical quality. Free from contaminants, our products provide maximum bioavailability.',
      stats: []
    },
    {
      image: '/assets/banners/Rectangle_205.png',
      title: 'ZeroHarm for Farmer',
      subtitle: 'Seeding change',
      description: 'ZeroHarm is helping farmers in the north-east adopt agronomical practices to optimise crop health, quality and yield. From soil management to crop rotation, we integrate our knowledge of social science, plant genetics and environmental products to provide efficient, responsible and economically viable alternatives for the farmer community.',
      stats: [
        { label: 'Farmers Connect', value: '10,000' },
        { label: 'Give us advantage', value: 'ESG' },
        { label: 'Increase in North East region', value: 'Goodwill' }
      ]
    }
  ];

  // Get theme assets from environment
  const themeAssets = getThemeAssets();

  // Clear all cookies and localStorage on login page load
  useEffect(() => {
    // Clear all cookies related to .scaledino.com domain
    const clearCookies = () => {
      const cookies = document.cookie.split(';');

      for (let cookie of cookies) {
        const cookieName = cookie.split('=')[0].trim();

        // Clear for current domain
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;

        // Clear for .zeroharm.in domain
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.zeroharm.in`;

        // Clear for specific subdomains
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=zeroharm.in`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.crm.zeroharm.in`;
      }
    };

    // Clear specific localStorage items
    const clearLocalStorage = () => {
      const itemsToRemove = [
        'crm_user',
        'crm_user_column_preferences',
        'crm_user_data'
      ];

      itemsToRemove.forEach(item => {
        localStorage.removeItem(item);
      });
    };

    // Execute cleanup
    clearCookies();
    clearLocalStorage();

    console.log('🧹 Cleared all cookies and localStorage on login page');
  }, []); // Run only once on component mount

  // Auto-rotate carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % bannerData.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [bannerData.length]);

  // Check for error in URL parameters (from OAuth redirects)
  useEffect(() => {
    const errorParam = searchParams.get('error');
    const emailParam = searchParams.get('email');

    if (errorParam) {
      let errorMessage = decodeURIComponent(errorParam);
      if (emailParam) {
        errorMessage += ` (${decodeURIComponent(emailParam)})`;
      }
      setError(errorMessage);

      // Clean the URL
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('error');
      newUrl.searchParams.delete('email');
      window.history.replaceState({}, document.title, newUrl.pathname);
    }
  }, [searchParams]);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
      const response = await fetch(`${apiUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }

      if (data.status === 'success' && data.data?.token) {
        // Use AuthService to handle token and user data (consistent with OAuth)
        const expiryDays = rememberMe ? 30 : 7;
        AuthService.setToken(data.data.token, { expires: expiryDays });
        AuthService.setUserData(data.data.user);

        // Store user info in localStorage for backward compatibility
        localStorage.setItem('crm_user', JSON.stringify(data.data.user));

        // Navigate to leads page
        navigate('/leads');
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err: any) {
      setError(err.message || 'Login failed. Please try again.');
      console.error('Error during login:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // Google OAuth URL
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || '648675563452-ffo4t8hkfovc1kq0qfmls26hvcn76u5o.apps.googleusercontent.com';
    const redirectUri = encodeURIComponent(import.meta.env.VITE_GOOGLE_CALLBACK_URL || 'http://localhost:8000/api/auth/google/callback');
    const state = Math.random().toString(36).substring(7); // Generate random state for security

    // Store state in sessionStorage for verification
    sessionStorage.setItem('google_oauth_state', state);

    // Google OAuth URL with required scopes (correct endpoint)
    const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=email%20profile&state=${state}`;

    // Redirect to Google for authentication
    window.location.href = googleAuthUrl;
  };


  return (
    <div className="h-screen w-screen flex m-0 p-0">
      {/* Left Side - Image Carousel */}
      <div className="w-1/2 h-full relative overflow-hidden bg-gray-900">
        {/* Carousel Slides */}
        {bannerData.map((banner, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {/* Background Image */}
            <img
              src={banner.image}
              alt={banner.title}
              className="w-full h-full object-cover"
            />

            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>

            {/* Content Overlay */}
            <div className="absolute inset-0 flex flex-col justify-end p-12 text-white">
              <div className="max-w-2xl">
                {/* Subtitle */}
                <p className="text-sm font-medium text-green-300 mb-2 uppercase tracking-wider">
                  {banner.subtitle}
                </p>

                {/* Title */}
                <h2 className="text-4xl font-bold mb-4 leading-tight">
                  {banner.title}
                </h2>

                {/* Description */}
                <p className="text-base text-gray-200 leading-relaxed mb-6">
                  {banner.description}
                </p>

                {/* Stats (if available) */}
                {banner.stats.length > 0 && (
                  <div className="grid grid-cols-3 gap-6 mt-8">
                    {banner.stats.map((stat, idx) => (
                      <div key={idx} className="border-l-2 border-green-400 pl-4">
                        <p className="text-2xl font-bold text-white">{stat.value}</p>
                        <p className="text-sm text-gray-300">{stat.label}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {/* Carousel Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
          {bannerData.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? 'bg-white w-8'
                  : 'bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={() => setCurrentSlide((prev) => (prev - 1 + bannerData.length) % bannerData.length)}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white p-3 rounded-full transition-all duration-200 z-20"
          aria-label="Previous slide"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button
          onClick={() => setCurrentSlide((prev) => (prev + 1) % bannerData.length)}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white p-3 rounded-full transition-all duration-200 z-20"
          aria-label="Next slide"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-1/2 h-full bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center">
        <div className="w-full max-w-md mx-8">
          <div className="">
            <div className="text-center mb-6">
              <img
                src={themeAssets.loginLogo}
                alt={themeAssets.brandName}
                className="h-12 mx-auto mb-4"
              />
              <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-700 to-gray-900 bg-clip-text text-transparent mb-2">
                Welcome to {themeAssets.brandName}
              </h1>
              <p className="text-gray-600">Sign in to your account</p>
            </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleEmailLogin} className="space-y-4">
            {/* Email/Password Login */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2 text-left">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2 text-left">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                >
                  {showPassword ? '👁️' : '🔒'}
                </button>
              </div>
            </div>

            {/* Remember Me Checkbox */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                  Remember me
                </label>
              </div>
              <div className="text-sm">
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-blue-600 hover:text-blue-700"
                >
                  Forgot password?
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full text-white py-3 px-4 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-200 shadow-lg"
              style={{
                backgroundColor: import.meta.env.VITE_BRAND_COLOR || '#153d23',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#0f2e1a';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = import.meta.env.VITE_BRAND_COLOR || '#153d23';
              }}
            >
              {isLoading ? 'Signing In...' : 'Sign In with Email'}
            </button>
          </form>

          {/* Social Login Divider */}
          <div className="my-6 flex items-center">
            <div className="flex-1 border-t border-gray-300"></div>
            <span className="px-4 text-sm text-gray-500">or continue with</span>
            <div className="flex-1 border-t border-gray-300"></div>
          </div>

          {/* Social Login Buttons */}
          <div className="flex justify-center">
            {/* Google Login */}
            <button
              onClick={handleGoogleLogin}
              disabled={isLoading}
              className="w-full bg-white hover:bg-gray-50 text-gray-900 py-3 px-4 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-200 shadow-lg flex items-center justify-center border border-gray-300"
              title="Login with Google"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span className="ml-2">Google</span>
            </button>
          </div>

          {/* Terms and Privacy */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              By continuing, you agree to our{' '}
              <a href="#" className="text-blue-600 hover:text-blue-700 transition-colors">
                Terms of Service
              </a>
              {' '}and{' '}
              <a href="#" className="text-blue-600 hover:text-blue-700 transition-colors">
                Privacy Policy
              </a>
            </p>
          </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;